import React, { useCallback, useEffect, useRef, useState } from "react";

const DEBOUNCE_MS = 300;

function EntityMultiSelect({
  label,
  placeholder,
  searchUrl,
  selected,
  onChange,
  noResultsLabel,
  searchingLabel,
  clearAllLabel,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const debounceRef = useRef(null);
  const fetchIdRef = useRef(0);
  const containerRef = useRef(null);

  const selectedIds = selected.map((item) => String(item.id));

  const performSearch = useCallback(
    (term) => {
      const currentFetchId = ++fetchIdRef.current;
      setIsSearching(true);

      const url = `${searchUrl}.json?q=${encodeURIComponent(term)}`;
      fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((json) => {
          if (currentFetchId !== fetchIdRef.current) return;
          const data = (json && json.data) || [];
          setResults(data.filter((item) => selectedIds.indexOf(String(item.id)) === -1));
          setHasFetched(true);
          setIsOpen(true);
        })
        .catch(() => {
          if (currentFetchId !== fetchIdRef.current) return;
          setResults([]);
          setHasFetched(true);
        })
        .finally(() => {
          if (currentFetchId !== fetchIdRef.current) return;
          setIsSearching(false);
        });
    },
    [searchUrl, selectedIds]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value.trim()), DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (!hasFetched) performSearch(query.trim());
  };

  const handleSelect = (item) => {
    if (selectedIds.indexOf(String(item.id)) !== -1) return;
    onChange([...selected, item]);
    setQuery("");
    setResults((prev) => prev.filter((r) => String(r.id) !== String(item.id)));
    setIsOpen(false);
  };

  const handleRemove = (id) => {
    onChange(selected.filter((item) => String(item.id) !== String(id)));
  };

  const handleClearAll = () => {
    onChange([]);
    setQuery("");
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const showNoResults = isOpen && !isSearching && hasFetched && results.length === 0;

  return (
    <div className="analytics-entity-multiselect" ref={containerRef}>
      <div className="analytics-entity-multiselect__head">
        <label className="analytics-entity-multiselect__label">{label}</label>
        {selected.length > 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm analytics-entity-multiselect__clear-all"
            onClick={handleClearAll}
          >
            {clearAllLabel}
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="analytics-entity-multiselect__chips">
          {selected.map((item) => (
            <span key={item.id} className="analytics-entity-multiselect__chip">
              <span className="analytics-entity-multiselect__chip-name">{item.name}</span>
              <button
                type="button"
                className="analytics-entity-multiselect__chip-remove"
                onClick={() => handleRemove(item.id)}
                aria-label="remove"
              >
                <i className="zmdi zmdi-close" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="form-group analytics-entity-multiselect__input">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
        <i className="form-group__bar" />
      </div>

      {isOpen && (
        <div className="analytics-entity-multiselect__results">
          {isSearching && (
            <div className="analytics-entity-multiselect__status">
              <span className="spinner-border spinner-border-sm mr-2" />
              {searchingLabel}
            </div>
          )}
          {!isSearching && results.length > 0 && results.map((item) => (
            <div
              key={item.id}
              className="analytics-entity-multiselect__result"
              onClick={() => handleSelect(item)}
            >
              {item.name}
            </div>
          ))}
          {showNoResults && (
            <div className="analytics-entity-multiselect__status">{noResultsLabel}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default EntityMultiSelect;
